/**
 * Cron expression → plain language (#1032).
 *
 * The single describer for the whole app. It replaced three divergent copies:
 * describeCron in components/connectors/library/cronDescription.ts (a terse,
 * zone-less lookup table), describeSchedule in
 * components/connectors/schedule/describeSchedule.ts (#946), and the private
 * getCronDescription inside SchedulePreview.tsx — which this is lifted from,
 * being the only one that handled monthly and weekday/weekend forms.
 *
 * A cron expression is a RECURRING RULE, so the zone is rendered as the fixed
 * short label ("ET"), never an Intl-derived EDT/EST. See @/lib/time/zoneLabels.
 *
 * Total function: always returns a string. Callers that must distinguish
 * "no schedule" from "a schedule" guard the empty case themselves.
 */
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import { shortZoneLabel } from '@/lib/time/zoneLabels';
import { formatDaysOfWeek, parseDayOfWeekField } from './dayOfWeek';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 24-hour cron hour/minute → 12-hour clock with the zone's fixed short label. */
function to12Hour(hour: number, minute: number, tz: string): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const m = String(minute).padStart(2, '0');
  return `${h}:${m} ${suffix} ${shortZoneLabel(tz)}`;
}

export function describeCron(cron: string, tz: string = DEFAULT_SCHEDULE_TIMEZONE): string {
  const trimmed = cron.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return 'Invalid cron expression';

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Common patterns — minute-level (no specific time to convert)
  if (trimmed === '* * * * *') return 'Every minute';
  if (trimmed === '*/5 * * * *') return 'Every 5 minutes';
  if (trimmed === '*/10 * * * *') return 'Every 10 minutes';
  if (trimmed === '*/15 * * * *') return 'Every 15 minutes';
  if (trimmed === '*/30 * * * *') return 'Every 30 minutes';
  if (trimmed === '0 * * * *') return 'Every hour at minute 0';
  if (trimmed === '0 */2 * * *') return 'Every 2 hours';
  if (trimmed === '0 */4 * * *') return 'Every 4 hours';
  if (trimmed === '0 */6 * * *') return 'Every 6 hours';
  if (trimmed === '0 */12 * * *') return 'Every 12 hours';

  // Daily patterns
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    if (minute !== '*' && hour !== '*') {
      return `Daily at ${to12Hour(parseInt(hour), parseInt(minute), tz)}`;
    }
    if (hour !== '*') {
      return `Every day at ${to12Hour(parseInt(hour), 0, tz)}`;
    }
  }

  // Weekly patterns.
  //
  // The single-day check is guarded by /^\d$/ rather than an isNaN check on
  // parseInt: parseInt('1-5') is 1 and parseInt('0,6') is 0, so an unguarded
  // parseInt swallowed the range and pair forms below and rendered them as
  // "Every Monday" / "Every Sunday" (#1032).
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    if (/^\d$/.test(dayOfWeek)) {
      const dayNum = parseInt(dayOfWeek);
      if (dayNum >= 0 && dayNum <= 6) {
        const timeStr = hour !== '*' ? ` at ${to12Hour(parseInt(hour), parseInt(minute), tz)}` : '';
        return `Every ${DAY_NAMES[dayNum]}${timeStr}`;
      }
    }
    if (dayOfWeek === '1-5') {
      const timeStr = hour !== '*' ? ` at ${to12Hour(parseInt(hour), parseInt(minute), tz)}` : '';
      return `Every weekday${timeStr}`;
    }
    if (dayOfWeek === '0,6') {
      const timeStr = hour !== '*' ? ` at ${to12Hour(parseInt(hour), parseInt(minute), tz)}` : '';
      return `Every weekend${timeStr}`;
    }
    // Any other day list or range at a fixed time: the Schedule form's Daily
    // with a weekday subset (#2191). "0 1 * * 2-6" → "Daily at 1:00 AM ET on Tue–Sat".
    const days = parseDayOfWeekField(dayOfWeek);
    if (days && /^\d+$/.test(hour) && /^\d+$/.test(minute)) {
      const at = to12Hour(parseInt(hour), parseInt(minute), tz);
      if (days.length === 7) return `Daily at ${at}`;
      if (days.length === 1) return `Every ${DAY_NAMES[days[0]]} at ${at}`;
      return `Daily at ${at} on ${formatDaysOfWeek(days)}`;
    }
  }

  // Monthly patterns
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    const timeStr =
      hour !== '*' && minute !== '*' ? ` at ${to12Hour(parseInt(hour), parseInt(minute), tz)}` : '';
    if (dayOfMonth === '1') {
      return `First day of every month${timeStr}`;
    }
    if (dayOfMonth === 'L') {
      return `Last day of every month${timeStr}`;
    }
    // Nearest-weekday token (croniter 6.2.4+; #1107), mirroring the backend
    // get_cron_description W branch (#1047). "15W" → the weekday nearest the
    // 15th. Guarded on an all-digit remainder so "LW" (which croniter rejects
    // anyway) falls through to the plain arm rather than rendering
    // "Weekday nearest day L…".
    if (dayOfMonth.endsWith('W') && /^\d+$/.test(dayOfMonth.slice(0, -1))) {
      const dom = dayOfMonth.slice(0, -1);
      return `Weekday nearest day ${dom} of every month${timeStr}`;
    }
    return `Day ${dayOfMonth} of every month${timeStr}`;
  }

  // Generic description. Known wart: lowercase and terse ("at minute 0, at
  // hour 3"). Preserved verbatim from the lifted implementation so this
  // consolidation is behavior-preserving; improving it is follow-up work.
  const parts_desc: string[] = [];

  if (minute === '*') parts_desc.push('every minute');
  else if (minute.includes('/')) parts_desc.push(`every ${minute.split('/')[1]} minutes`);
  else parts_desc.push(`at minute ${minute}`);

  if (hour === '*') {
    /* implied */
  } else if (hour.includes('/')) parts_desc.push(`every ${hour.split('/')[1]} hours`);
  else parts_desc.push(`at hour ${hour}`);

  return parts_desc.join(', ') || trimmed;
}
